import { type FC, useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useAppStore } from '@/stores/app-store';
import { formatCompact } from '@/utils/format';
import type { MarketCategory, MarketQuote } from '@/types';

// ---------------------------------------------------------------------------
// Market Pulse Panel — 9 categories, horizontal ticker carousel, sub-filters
// ---------------------------------------------------------------------------

interface TabDef { id: MarketCategory; label: string; icon: string; }

const TABS: TabDef[] = [
  { id: 'equity',    label: 'Equity',   icon: '📈' },
  { id: 'bonds',     label: 'Bonds',    icon: '🔔' },
  { id: 'crypto',    label: 'Crypto',   icon: '₿'  },
  { id: 'forex',     label: 'Forex',    icon: '💱' },
  { id: 'energy',    label: 'Energy',   icon: '⚡' },
  { id: 'commodity', label: 'Metals',   icon: '⛏'  },
  { id: 'defense',   label: 'Defense',  icon: '🛡'  },
  { id: 'food',      label: 'Food/BDI', icon: '🌾' },
  { id: 'macro',     label: 'Macro',    icon: '📊' },
];

// Sub-filter definitions for equity tab
const EQUITY_SUB_FILTERS = [
  { id: 'all',       label: 'All'    },
  { id: 'us-index',  label: 'US Idx' },
  { id: 'us-mega',   label: 'US Tech'},
  { id: 'finance',   label: 'Fin.'   },
  { id: 'asia',      label: 'Asia'   },
  { id: 'europe',    label: 'EU/EM'  },
  { id: 'defense',   label: 'Def.'   },
];

// Sub-filter for bonds tab
const BOND_SUB_FILTERS = [
  { id: 'all',      label: 'All'      },
  { id: 'yield',    label: 'Yields'   },
  { id: 'bond-etf', label: 'ETFs'     },
];

// Inverse symbols (rising = bad)
const INVERSE_SYMS = new Set(['VIX', 'GPR', 'EMBIG', '^VIX']);

function formatPrice(q: MarketQuote): string {
  if (q.unit === 'KRW') return `₩${formatCompact(q.price)}`;
  if (q.unit === 'bps') return `${q.price.toFixed(0)}bps`;
  if (q.unit === 'index') return q.price >= 10000 ? formatCompact(q.price) : q.price.toFixed(0);
  if (q.unit === '%') return `${q.price.toFixed(2)}%`;
  if (q.price >= 1000) return `$${formatCompact(q.price)}`;
  if (q.price >= 1) return `$${q.price.toFixed(2)}`;
  return `$${q.price.toFixed(4)}`;
}

function formatChange(q: MarketQuote): string {
  const sign = q.changePercent >= 0 ? '+' : '';
  return `${sign}${q.changePercent.toFixed(2)}%`;
}

function isPositive(q: MarketQuote): boolean {
  return INVERSE_SYMS.has(q.symbol) ? q.changePercent <= 0 : q.changePercent >= 0;
}

function displaySym(sym: string): string {
  return sym.replace(/^\^/, '').replace('000001.SS', 'SSE');
}

// ---------------------------------------------------------------------------
// Horizontal Ticker Carousel
// ---------------------------------------------------------------------------
const TickerCarousel: FC<{
  quotes: MarketQuote[];
  selected: string | null;
  onSelect: (sym: string) => void;
}> = ({ quotes, selected, onSelect }) => {
  const ref = useRef<HTMLDivElement>(null);

  const scroll = useCallback((dir: 'l' | 'r') => {
    if (ref.current) ref.current.scrollBy({ left: dir === 'l' ? -120 : 120, behavior: 'smooth' });
  }, []);

  if (quotes.length === 0) return null;

  return (
    <div className="relative flex items-center border-b border-slate-800/40 shrink-0">
      {/* Left arrow */}
      <button
        onClick={() => scroll('l')}
        className="absolute left-0 z-10 h-full w-6 flex items-center justify-center bg-gradient-to-r from-[#080d14] via-[#080d14]/90 to-transparent text-slate-300 hover:text-neon-cyan text-base font-bold transition-colors"
        title="Scroll left"
      >‹</button>

      {/* Scrollable strip */}
      <div
        ref={ref}
        className="flex gap-0.5 overflow-x-auto px-6 py-1"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {quotes.map((q) => {
          const pos = isPositive(q);
          const active = selected === q.symbol;
          return (
            <button
              key={q.symbol}
              onClick={() => onSelect(q.symbol === selected ? '' : q.symbol)}
              className={clsx(
                'shrink-0 flex flex-col items-center px-1.5 py-0.5 rounded transition-all min-w-[44px]',
                active ? 'bg-brand/20 ring-1 ring-brand/40' : 'hover:bg-white/5',
              )}
            >
              <span className={clsx('text-[8px] font-bold leading-tight', active ? 'text-brand-light' : 'text-slate-400')}>
                {displaySym(q.symbol)}
              </span>
              <span className={clsx('text-[8px] font-semibold leading-tight tabular-nums', pos ? 'text-emerald-400' : 'text-red-400')}>
                {pos ? '▲' : '▼'}{Math.abs(q.changePercent).toFixed(1)}%
              </span>
            </button>
          );
        })}
      </div>

      {/* Right arrow */}
      <button
        onClick={() => scroll('r')}
        className="absolute right-0 z-10 h-full w-6 flex items-center justify-center bg-gradient-to-l from-[#080d14] via-[#080d14]/90 to-transparent text-slate-300 hover:text-neon-cyan text-base font-bold transition-colors"
        title="Scroll right"
      >›</button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Selected Ticker Detail Card
// ---------------------------------------------------------------------------
const SelectedDetail: FC<{ quote: MarketQuote }> = ({ quote }) => {
  const pos = isPositive(quote);
  return (
    <div className="px-3 py-1.5 border-b border-slate-800/40 bg-slate-900/30 shrink-0">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-bold text-slate-200 font-mono">{displaySym(quote.symbol)}</span>
            {quote.subCategory && (
              <span className="text-[7px] px-1 rounded bg-slate-800 text-slate-500 uppercase">{quote.subCategory}</span>
            )}
          </div>
          <div className="text-[9px] text-slate-500 truncate max-w-[140px]">{quote.name}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[13px] font-bold text-slate-100 tabular-nums leading-tight">{formatPrice(quote)}</div>
          <div className={clsx('text-[10px] font-semibold tabular-nums', pos ? 'text-emerald-400' : 'text-red-400')}>
            {formatChange(quote)}
          </div>
        </div>
      </div>
      {(quote.high52 || quote.low52 || quote.marketCap) && (
        <div className="flex items-center gap-2 mt-0.5">
          {quote.high52 && quote.low52 && (
            <span className="text-[8px] text-slate-600">
              52W: <span className="text-red-500">${formatCompact(quote.low52)}</span>
              {' – '}
              <span className="text-emerald-500">${formatCompact(quote.high52)}</span>
            </span>
          )}
          {quote.marketCap && (
            <span className="text-[8px] text-slate-600">
              Cap: <span className="text-slate-400">${formatCompact(quote.marketCap)}</span>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Quote Row (vertical list)
// ---------------------------------------------------------------------------
const QuoteRow: FC<{ quote: MarketQuote; selected: boolean; onClick: () => void }> = ({ quote, selected, onClick }) => {
  const pos = isPositive(quote);
  return (
    <button
      className={clsx(
        'data-row justify-between w-full text-left transition-colors',
        selected && 'bg-brand/10',
      )}
      onClick={onClick}
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-[9px] font-mono font-semibold text-slate-400 w-12 shrink-0 truncate">{displaySym(quote.symbol)}</span>
        <span className="text-[10px] text-slate-300 truncate">{quote.name}</span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-[11px] text-slate-200 font-medium tabular-nums">{formatPrice(quote)}</span>
        <span className={clsx('text-[10px] font-semibold tabular-nums w-14 text-right', pos ? 'text-emerald-400' : 'text-red-400')}>
          {formatChange(quote)}
        </span>
      </div>
    </button>
  );
};

// ---------------------------------------------------------------------------
// Main Panel
// ---------------------------------------------------------------------------
export const MarketPanel: FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<MarketCategory>('equity');
  const [equitySub, setEquitySub] = useState('all');
  const [bondSub, setBondSub] = useState('all');
  const [selectedSym, setSelectedSym] = useState<string | null>(null);
  const marketQuotes = useAppStore((s) => s.marketQuotes);

  const tabQuotes = useMemo(
    () => marketQuotes.filter((q) => q.category === activeTab),
    [marketQuotes, activeTab],
  );

  const filteredQuotes = useMemo(() => {
    if (activeTab === 'equity') {
      return equitySub === 'all' ? tabQuotes : tabQuotes.filter((q) => q.subCategory === equitySub);
    }
    if (activeTab === 'bonds') {
      return bondSub === 'all' ? tabQuotes : tabQuotes.filter((q) => q.subCategory === bondSub);
    }
    return tabQuotes;
  }, [tabQuotes, activeTab, equitySub, bondSub]);

  const selectedQuote = useMemo(
    () => (selectedSym ? filteredQuotes.find((q) => q.symbol === selectedSym) ?? null : null),
    [filteredQuotes, selectedSym],
  );

  const handleTabChange = (tab: MarketCategory) => {
    setActiveTab(tab);
    setSelectedSym(null);
    setEquitySub('all');
    setBondSub('all');
  };

  const handleSelect = (sym: string) => setSelectedSym(sym || null);

  // Ref + scroll for tab bar arrows
  const tabBarRef = useRef<HTMLDivElement>(null);
  const [tabCanScrollL, setTabCanScrollL] = useState(false);
  const [tabCanScrollR, setTabCanScrollR] = useState(false);
  const scrollTab = useCallback((dir: 'l' | 'r') => {
    tabBarRef.current?.scrollBy({ left: dir === 'l' ? -100 : 100, behavior: 'smooth' });
  }, []);
  const updateTabArrows = useCallback(() => {
    const el = tabBarRef.current;
    if (!el) return;
    setTabCanScrollL(el.scrollLeft > 4);
    setTabCanScrollR(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);
  useEffect(() => {
    const el = tabBarRef.current;
    if (!el) return;
    updateTabArrows();
    el.addEventListener('scroll', updateTabArrows, { passive: true });
    const ro = new ResizeObserver(updateTabArrows);
    ro.observe(el);
    return () => { el.removeEventListener('scroll', updateTabArrows); ro.disconnect(); };
  }, [updateTabArrows]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="panel-header shrink-0">
        <span className="panel-title">{t('panels.market')}</span>
        <div className="flex items-center gap-2">
          {activeTab === 'equity' && marketQuotes.some((q) => q.category === 'equity' && q.high52) && (
            <span className="text-[8px] text-emerald-500 font-medium">● LIVE</span>
          )}
          <span className="text-[10px] text-slate-500">{marketQuotes.length} instruments</span>
        </div>
      </div>

      {/* Tab bar with scroll arrows */}
      <div className="relative flex items-center border-b border-surface-border shrink-0">
        {tabCanScrollL && (
          <button
            onClick={() => scrollTab('l')}
            className="absolute left-0 z-10 h-full w-7 flex items-center justify-center bg-gradient-to-r from-[#080d14] via-[#080d14]/95 to-transparent text-neon-cyan hover:text-white text-lg font-bold shrink-0 animate-pulse transition-colors"
            title="More tabs"
          >‹</button>
        )}
        <div
          ref={tabBarRef}
          className="flex overflow-x-auto"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {TABS.map((tab) => {
            const count = marketQuotes.filter((q) => q.category === tab.id).length;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={clsx(
                  'flex items-center gap-1 px-2 py-1.5 text-[10px] font-medium whitespace-nowrap transition-colors border-b-2 shrink-0',
                  activeTab === tab.id
                    ? 'border-brand text-brand-light bg-brand/10'
                    : 'border-transparent text-slate-500 hover:text-slate-300 hover:bg-surface-hover',
                )}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
                {count > 0 && (
                  <span className={clsx('text-[8px]', activeTab === tab.id ? 'text-brand-light' : 'text-slate-700')}>{count}</span>
                )}
              </button>
            );
          })}
        </div>
        {tabCanScrollR && (
          <button
            onClick={() => scrollTab('r')}
            className="absolute right-0 z-10 h-full w-7 flex items-center justify-center bg-gradient-to-l from-[#080d14] via-[#080d14]/95 to-transparent text-neon-cyan hover:text-white text-lg font-bold shrink-0 animate-pulse transition-colors"
            title="More tabs"
          >›</button>
        )}
      </div>

      {/* Equity sub-filter */}
      {activeTab === 'equity' && (
        <div className="flex gap-0.5 px-2 py-1 border-b border-slate-800/40 shrink-0 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
          {EQUITY_SUB_FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => { setEquitySub(f.id); setSelectedSym(null); }}
              className={clsx(
                'shrink-0 px-1.5 py-0.5 rounded text-[9px] font-medium transition-colors',
                equitySub === f.id ? 'bg-brand/20 text-brand-light' : 'text-slate-500 hover:text-slate-300',
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}

      {/* Bonds sub-filter */}
      {activeTab === 'bonds' && (
        <div className="flex gap-0.5 px-2 py-1 border-b border-slate-800/40 shrink-0">
          {BOND_SUB_FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => { setBondSub(f.id); setSelectedSym(null); }}
              className={clsx(
                'shrink-0 px-1.5 py-0.5 rounded text-[9px] font-medium transition-colors',
                bondSub === f.id ? 'bg-brand/20 text-brand-light' : 'text-slate-500 hover:text-slate-300',
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}

      {/* Horizontal ticker carousel */}
      <TickerCarousel quotes={filteredQuotes} selected={selectedSym} onSelect={handleSelect} />

      {/* Selected detail */}
      {selectedQuote && <SelectedDetail quote={selectedQuote} />}

      {/* Quotes list */}
      <div className="overflow-y-auto flex-1 px-2 py-1 space-y-0.5">
        {filteredQuotes.length === 0 ? (
          <div className="flex items-center justify-center h-12 text-xs text-slate-600">Loading…</div>
        ) : (
          filteredQuotes.map((q) => (
            <QuoteRow
              key={q.symbol}
              quote={q}
              selected={selectedSym === q.symbol}
              onClick={() => handleSelect(selectedSym === q.symbol ? '' : q.symbol)}
            />
          ))
        )}
      </div>
    </div>
  );
};
